import { CopyIcon, PlugZapIcon, RefreshCwIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import type { HostedPairingCode, HostedStudioStatus } from "../../hosted/api";
import { resolveStudioPresentation } from "../../hosted/studioPresentation";
import { hostedApiErrorMessage, useHostedApiClient } from "../../hosted/useHostedApiClient";
import {
  browserVisibilityPollingHost,
  startVisibilityAwarePolling,
} from "../../hosted/visibilityPolling";

const STUDIO_STATUS_POLL_MS = 10_000;

const TONE_BADGE_VARIANT = {
  connected: "success",
  warning: "warning",
  idle: "outline",
} as const;

/**
 * Studio connection state for one hosted project, plus the pairing code the
 * Studio plugin needs. Polls on the plugin's heartbeat cadence so a live
 * connection shows up without a manual refresh.
 */
export function HostedStudioPanel({ projectId }: { readonly projectId: string }) {
  const client = useHostedApiClient();
  const [status, setStatus] = useState<HostedStudioStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [pairing, setPairing] = useState<HostedPairingCode | null>(null);
  const [pairingError, setPairingError] = useState<string | null>(null);
  const [isPairing, setIsPairing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const copyResetRef = useRef<number | null>(null);
  // The project this panel is currently reading for. A status read is a network
  // round trip, so one issued for a previous project can resolve after the user
  // switches and would otherwise show that project's Studio state here.
  const projectIdRef = useRef(projectId);

  const loadStatus = useCallback(async () => {
    projectIdRef.current = projectId;
    try {
      const next = await client.getStudioStatus(projectId);
      if (projectIdRef.current !== projectId) {
        return;
      }
      setStatus(next);
      setStatusError(null);
    } catch (error) {
      if (projectIdRef.current !== projectId) {
        return;
      }
      setStatusError(hostedApiErrorMessage(error));
    }
    setNow(Date.now());
  }, [client, projectId]);

  useEffect(() => {
    setStatus(null);
    setStatusError(null);
    setPairing(null);
    setPairingError(null);
    void loadStatus();
    return startVisibilityAwarePolling({
      poll: () => void loadStatus(),
      intervalMs: STUDIO_STATUS_POLL_MS,
      host: browserVisibilityPollingHost(),
    });
  }, [loadStatus]);

  useEffect(
    () => () => {
      if (copyResetRef.current !== null) {
        window.clearTimeout(copyResetRef.current);
      }
    },
    [],
  );

  const requestPairingCode = () => {
    if (isPairing) return;
    setIsPairing(true);
    setPairingError(null);
    void (async () => {
      try {
        setPairing(await client.createPairingCode(projectId));
      } catch (error) {
        setPairingError(hostedApiErrorMessage(error));
      } finally {
        setIsPairing(false);
      }
    })();
  };

  const copyPairingCode = () => {
    const code = pairing?.code;
    if (!code) return;
    void navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(true);
        if (copyResetRef.current !== null) {
          window.clearTimeout(copyResetRef.current);
        }
        copyResetRef.current = window.setTimeout(() => setCopied(false), 1500);
      },
      () => setPairingError("Could not copy the pairing code."),
    );
  };

  const presentation = resolveStudioPresentation({ status, now });

  return (
    <section
      aria-labelledby="hosted-studio-heading"
      className="rounded-xl border border-border/70 bg-card/25 p-4"
      data-testid="hosted-studio-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-medium text-foreground" id="hosted-studio-heading">
            Roblox Studio
          </h2>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground/80">
            {presentation.detail}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge size="sm" variant={TONE_BADGE_VARIANT[presentation.tone]}>
            {presentation.label}
          </Badge>
          <Button
            aria-label="Refresh Studio status"
            size="icon-xs"
            variant="ghost"
            className="size-5 text-muted-foreground hover:text-foreground"
            onClick={() => void loadStatus()}
          >
            <RefreshCwIcon className="size-3" />
          </Button>
        </div>
      </div>

      {statusError ? (
        <p className="mt-3 text-xs text-destructive-foreground" role="alert">
          {statusError}
        </p>
      ) : null}

      <div className="mt-4">
        {pairing ? (
          <div className="flex flex-wrap items-center gap-2">
            <code
              className="rounded-md border border-border/70 bg-background/70 px-2.5 py-1 font-mono text-sm tracking-[0.2em] text-foreground"
              data-testid="hosted-pairing-code"
            >
              {pairing.code}
            </code>
            <Button size="xs" variant="outline" onClick={copyPairingCode}>
              <CopyIcon className="size-3" />
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button size="xs" variant="ghost" disabled={isPairing} onClick={requestPairingCode}>
              New code
            </Button>
            <span className="text-xs text-muted-foreground/70">
              Enter this in the Studio plugin widget.
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={isPairing} onClick={requestPairingCode}>
              {isPairing ? <Spinner className="size-4" /> : <PlugZapIcon className="size-4" />}
              {isPairing ? "Creating code…" : "Pair Studio"}
            </Button>
            <span className="text-xs text-muted-foreground/70">
              Creates a short-lived code for the Studio plugin.
            </span>
          </div>
        )}
        {pairingError ? (
          <p className="mt-2 text-xs text-destructive-foreground" role="alert">
            {pairingError}
          </p>
        ) : null}
      </div>
    </section>
  );
}
